import React, { useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import { Alert, Box, Chip, Divider, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

import TabPanelShell from '~/components/Offers/Detail/OfferBuilder/shared/TabPanelShell';

import { useGetAccountAssets } from '~/hooks/apis/offers/useGetAccountAssets';
import { useGetOffer } from '~/hooks/apis/offers/useGetOffer';
import { useGetOpportunityRelated } from '~/hooks/apis/offers/useGetOpportunityRelated';

import {
  useInclusionDraftsContext,
  usePackageOptionsContext,
  usePricingToolSession,
  usePropertySetupContext,
} from '../../PricingToolSessionContext';
import { PRICING_OPTION_LABELS } from '../../constants';
import { DEFAULT_OPTION_STATE, collectRoomAssets } from '../packageOptions/packageOptionsModel';

import DealOptionInclusionsView from './DealOptionInclusionsView';
import InclusionsEditView from './InclusionsEditView';
import { INCLUSION_APPLIES_OPTIONS, INCLUSION_TYPE_OPTIONS } from './fields';
import {
  buildPackageInclusionGroups,
  copyDraftsToOption,
  defaultApplyTo,
  getAvailableNights,
  getPackageRooms,
} from './inclusionsModel';

interface Props {
  offerId?: string;
}

type ViewMode = 'edit' | 'list';

export default function InclusionsTab({ offerId }: Props) {
  const { isLoading, error: relatedError } = useGetOpportunityRelated(offerId);
  const { data: offer, isLoading: isLoadingOffer, error: offerError } = useGetOffer(offerId);
  const {
    data: accountAssets,
    isLoading: isLoadingAssets,
    error: assetsError,
  } = useGetAccountAssets(offer?.vendor_account_id, 'services');
  const {
    drafts,
    inclusionAssets,
    addDraft,
    updateDraft,
    removeDraft,
    replaceOptionDrafts,
    linkAssetByName,
    createInclusionAsset,
  } = useInclusionDraftsContext();
  // See docs/business-rules.md#inclusion-assets.
  const { draftIdBySfid } = usePricingToolSession();
  const assets = useMemo(
    () => collectRoomAssets(accountAssets ?? [], draftIdBySfid, inclusionAssets),
    [accountAssets, draftIdBySfid, inclusionAssets],
  );
  const linkByName = (name: string) => linkAssetByName(name, assets);
  const { optionStates } = usePackageOptionsContext();
  const { enqueueSnackbar } = useSnackbar();

  const [view, setView] = useState<ViewMode>('edit');
  const [activeOption, setActiveOption] = useState(0);

  const {
    fx: { currency },
  } = usePropertySetupContext();

  const packageOption = optionStates[activeOption] ?? DEFAULT_OPTION_STATE;
  const rooms = useMemo(() => getPackageRooms(packageOption), [packageOption]);
  const nights = useMemo(() => getAvailableNights(packageOption), [packageOption]);

  const optionDrafts = drafts.filter((draft) => draft.pricingOptionIndex === activeOption);

  const addBlankInclusion = (prefill: { name?: string; type?: string } = {}) => {
    addDraft({
      pricingOptionIndex: activeOption,
      name: prefill.name ?? '',
      description: '',
      type: prefill.type ?? INCLUSION_TYPE_OPTIONS[0],
      applies: INCLUSION_APPLIES_OPTIONS[0],
      validFor: ['Adult'],
      prv: undefined,
      assetLink: prefill.name ? linkByName(prefill.name) : undefined,
      applyTo: defaultApplyTo(),
    });
  };

  const handleCopyFromFirstOption = () => {
    const sourceDrafts = drafts.filter((draft) => draft.pricingOptionIndex === 0);
    const copied = copyDraftsToOption(sourceDrafts, optionStates[0] ?? DEFAULT_OPTION_STATE, packageOption);
    replaceOptionDrafts(activeOption, copied);

    const from = PRICING_OPTION_LABELS[0];
    const to = PRICING_OPTION_LABELS[activeOption];
    if (copied.length < sourceDrafts.length) {
      enqueueSnackbar(
        `Copied ${copied.length} of ${sourceDrafts.length} inclusions - the rest don't match any package on ${to}`,
        {
          variant: 'warning',
        },
      );
    } else {
      enqueueSnackbar(`Copied over inclusions from ${from} to ${to}`, { variant: 'info' });
    }
  };

  if (isLoading || isLoadingOffer || isLoadingAssets) {
    return null;
  }

  return (
    <TabPanelShell
      title="Inclusions"
      subtitle="Add inclusions and see what each one is worth - the lever that lifts PRV without cutting price."
      actions={
        <ToggleButtonGroup size="small" exclusive value={view} onChange={(_event, next) => next && setView(next)}>
          <ToggleButton value="edit">Edit</ToggleButton>
          <ToggleButton value="list">List</ToggleButton>
        </ToggleButtonGroup>
      }
    >
      <Stack spacing={2}>
        {relatedError && (
          <Alert severity="error">
            Unable to load synced deals. Known deal options and the opportunity currency may be incomplete.
          </Alert>
        )}
        {(offerError || assetsError) && (
          <Alert severity="error">Unable to load account assets. Inclusion assets may be incomplete.</Alert>
        )}

        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
            Inclusion assets
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {assets.length === 0 ? (
              <Typography variant="caption" color="text.secondary" fontStyle="italic">
                No inclusion assets yet - create one from an inclusion&apos;s Asset field
              </Typography>
            ) : (
              assets.map((asset) => <Chip key={asset.id} label={asset.name} size="small" />)
            )}
          </Stack>
        </Box>

        <Divider />

        <ToggleButtonGroup
          size="small"
          exclusive
          value={activeOption}
          onChange={(_event, next) => next !== null && setActiveOption(next)}
        >
          {PRICING_OPTION_LABELS.map((_, index) => (
            <ToggleButton key={index} value={index}>
              {PRICING_OPTION_LABELS[index]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {view === 'edit' && (
          <InclusionsEditView
            assets={assets}
            currency={currency}
            drafts={optionDrafts}
            onCreateAsset={createInclusionAsset}
            nights={nights}
            onAddInclusion={addBlankInclusion}
            onCopyFromFirstOption={activeOption > 0 ? handleCopyFromFirstOption : undefined}
            onRemoveDraft={removeDraft}
            onUpdateDraft={updateDraft}
            rooms={rooms}
            sourceOptionLabel={PRICING_OPTION_LABELS[0]}
            targetOptionLabel={PRICING_OPTION_LABELS[activeOption]}
          />
        )}

        {view === 'list' && (
          <DealOptionInclusionsView
            groups={buildPackageInclusionGroups(packageOption, optionDrafts)}
            currency={currency}
            optionLabel={PRICING_OPTION_LABELS[activeOption]}
          />
        )}
      </Stack>
    </TabPanelShell>
  );
}
